import React, { useState, useMemo, useCallback } from "react";
import ThinScroll from "../../shared/ThinScroll";
import Pagination from "../../shared/Pagination";
import useTablePage from "../../../hooks/useTablePage";
import { fmtCurrency } from "../../shared/format.js";

/**
 * DetailRangePanel — Extended-range day-by-day weather table (Detail tab).
 *
 * Extracted from WeatherIntelligencePage.js. Restyled to mirror DataTable
 * (raw_daily evidence component): same COLUMNS-driven header, ThinScroll +
 * responsive-card-table + table-fixed shell, colour-coded cells, weekday +
 * short date in the date cell, and the shared Pagination component backed by
 * useTablePage for clamped + auto-resetting page state.
 *
 * Keeps the panel's own chrome that DataTable does not have: the panel-style
 * wrapper, the From/To date pickers (with 400-day auto-clamp), the max-range
 * warning, the "N days · from → to" summary line, and loading/error/empty
 * states.
 *
 * Props:
 *   detailData   — fn_get_weather_detail result wrapper ({ data, loading, error })
 *   detailRange  — fn_get_weather_dates result (has detail_from / detail_to)
 *   onFromChange — parent setter for the "from" date
 *   onToChange   — parent setter for the "to" date
 *   detailFrom   — current "from" date (YYYY-MM-DD)
 *   detailTo     — current "to" date (YYYY-MM-DD)
 */

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format a YYYY-MM-DD string for display without timezone conversion. */
const fmtDisplayDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
};

/** Format number with locale separators. */
const fmtNum = (v) => (v != null ? Number(v).toLocaleString() : "—");

/** Short date — "Aug 10" */
const shortDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Day of week — "Sun" */
const dayOfWeek = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short" });
};

/** Rain % colour — higher is wetter. */
const rainColor = (pct) => {
  if (pct >= 50) return "text-blue-500 dark:text-blue-400";
  if (pct >= 25) return "text-yellow-500 dark:text-yellow-400";
  return "text-gray-700 dark:text-gray-300";
};

/** Format a Date as YYYY-MM-DD (local, no timezone shift). */
const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Known Supabase RPC error codes → friendly copy; unknown codes fall back to a
// generic message. (Mirrors WeatherIntelligencePage's RPC_ERROR_MESSAGES so the
// extracted panel stays self-contained.)
const RPC_ERROR_MESSAGES = {
  "57014": "The request took too long. Try selecting a shorter date range.",
  "57P03": "Could not connect to the server. Please try again later.",
  "42501": "You don't have permission to access this data.",
  "42P01": "Data not found for this property.",
  "28P01": "Authentication failed. Please try again.",
  "PGRST204": "The requested data format is not available.",
};

// ─── Columns ─────────────────────────────────────────────────────────────────
// 15 columns for the extended range view (includes Normal, vs Normal, Rain Yrs,
// and Snow that raw_daily omits). Widths sum to 1255px, matching the table's
// min-w-[1255px] so columns keep their declared width until the container
// narrows enough to scroll horizontally.
const COLUMNS = [
  { key: "date",                    label: "Date",      align: "left",   width: "w-[120px]" },
  { key: "temp_high_f",             label: "High",      align: "right",  width: "w-[70px]" },
  { key: "typical_high_f",          label: "Normal",    align: "right",  width: "w-[70px]",  sortable: true },
  { key: "vs_typical_f",            label: "vs Normal", align: "right",  width: "w-[80px]",  sortable: true },
  { key: "temp_low_f",              label: "Low",       align: "right",  width: "w-[70px]" },
  { key: "precip_probability_pct",  label: "Rain %",    align: "right",  width: "w-[70px]" },
  { key: "rain_day_pct_normal",     label: "Rain Yrs",  align: "right",  width: "w-[75px]",  sortable: true },
  { key: "snow_cm",                 label: "Snow",      align: "right",  width: "w-[65px]" },
  { key: "precip_mm",               label: "Precip",    align: "right",  width: "w-[70px]" },
  { key: "wind_max_mph",            label: "Wind",      align: "right",  width: "w-[70px]" },
  { key: "weather",                 label: "Conditions", align: "left",  width: "w-[140px]" },
  { key: "source",                  label: "Source",    align: "center", width: "w-[90px]",  sortable: true },
  { key: "sessions",                label: "Sessions",  align: "right",  width: "w-[80px]" },
  { key: "bookings",                label: "Bookings",  align: "right",  width: "w-[80px]" },
  { key: "revenue",                 label: "Revenue",   align: "right",  width: "w-[100px]" },
];

const MAX_RANGE_DAYS = 400;
const PAGE_SIZE = 15;

// ─── CSV export (mirrors DataTable) ──────────────────────────────────────────
/** Build CSV string from rows. */
const toCSV = (rows) => {
  const header = "Date,High (°F),Normal (°F),vs Normal (°F),Low (°F),Rain %,Rain Yrs (%),Snow (cm),Precip (mm),Wind (mph),Conditions,Source,Sessions,Bookings,Revenue";
  const lines = rows.map((r) =>
    [
      r.date,
      r.temp_high_f != null ? Math.round(r.temp_high_f) : "",
      r.typical_high_f != null ? Math.round(r.typical_high_f) : "",
      r.vs_typical_f != null ? Number(r.vs_typical_f).toFixed(1) : "",
      r.temp_low_f != null ? Math.round(r.temp_low_f) : "",
      r.precip_probability_pct != null ? Math.round(r.precip_probability_pct) : "",
      r.rain_day_pct_normal ?? "",
      r.snow_cm ?? "",
      r.precip_mm ?? "",
      r.wind_max_mph ?? "",
      `"${(r.weather || "").replace(/"/g, '""')}"`,
      r.source ?? "",
      r.sessions ?? "",
      r.bookings ?? "",
      r.revenue ?? "",
    ].join(",")
  );
  return [header, ...lines].join("\n");
};

/** Trigger CSV download. */
const downloadCSV = (rows) => {
  const csv = toCSV(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "weather-detail-extended.csv";
  a.click();
  URL.revokeObjectURL(url);
};

/** Detect if the app is in dark mode (reads the <html> class toggled by the theme provider). */
const isDarkMode = () =>
  typeof document !== "undefined" && document.documentElement.classList.contains("dark");

// ─── Component ────────────────────────────────────────────────────────────────

const DetailRangePanel = ({ detailData, detailRange, onFromChange, onToChange, detailFrom, detailTo }) => {
  const detailRows = detailData.data?.rows || [];

  // ─── Sort (same as DataTable) ──────────────────────────────────────────────
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = useCallback((key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  }, [sortKey]);

  const sorted = useMemo(() => {
    const copy = [...detailRows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [detailRows, sortKey, sortDir]);

  // ─── Pagination (shared hook, same as DataTable) ───────────────────────────
  const totalItems = sorted.length;
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(
    totalItems,
    PAGE_SIZE,
    [detailRows, sortKey, sortDir],
  );
  const pageRows = sorted.slice(sliceStart, sliceEnd);

  // ─── Date-range warning ────────────────────────────────────────────────────
  const rangeDays = detailFrom && detailTo
    ? Math.round((new Date(detailTo + "T00:00:00") - new Date(detailFrom + "T00:00:00")) / 86400000)
    : 0;
  const isAtMaxRange = rangeDays >= MAX_RANGE_DAYS;

  // ─── Date validation (from the original inline panel) ───────────────────────
  const handleFromChange = (newFrom) => {
    if (!newFrom) {
      onFromChange(null);
      return;
    }
    // If newFrom is after current "To", move "To" to match
    if (detailTo && newFrom > detailTo) {
      onToChange(newFrom);
    }
    // If range exceeds 400 days, clamp "To" to 400 days after "From"
    if (detailTo) {
      const days = Math.round((new Date(detailTo + "T00:00:00") - new Date(newFrom + "T00:00:00")) / 86400000);
      if (days > MAX_RANGE_DAYS) {
        const clamped = new Date(newFrom + "T00:00:00");
        clamped.setDate(clamped.getDate() + MAX_RANGE_DAYS);
        onToChange(toISODate(clamped));
      }
    }
    onFromChange(newFrom);
  };

  const handleToChange = (newTo) => {
    if (!newTo) {
      onToChange(null);
      return;
    }
    // If newTo is before current "From", move "From" to match
    if (detailFrom && newTo < detailFrom) {
      onFromChange(newTo);
    }
    // If range exceeds 400 days, clamp "From" to 400 days before "To"
    if (detailFrom) {
      const days = Math.round((new Date(newTo + "T00:00:00") - new Date(detailFrom + "T00:00:00")) / 86400000);
      if (days > MAX_RANGE_DAYS) {
        const clamped = new Date(newTo + "T00:00:00");
        clamped.setDate(clamped.getDate() - MAX_RANGE_DAYS);
        onFromChange(toISODate(clamped));
      }
    }
    onToChange(newTo);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      <p className="text-sm font-bold text-gray-800 dark:text-gray-100">Extended Range</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-4">Extended date range for day-by-day weather data — reaches back further than the default view.</p>

      {/* ─── Date pickers ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          From:
        </label>
        <input
          type="date"
          value={detailFrom || ""}
          min={detailRange?.detail_from}
          max={detailRange?.detail_to}
          style={{ colorScheme: isDarkMode() ? 'dark' : 'light' }}
          onChange={(e) => handleFromChange(e.target.value || null)}
          className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[#32B1FF]"
        />
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          To:
        </label>
        <input
          type="date"
          value={detailTo || ""}
          min={detailFrom || detailRange?.detail_from}
          max={detailRange?.detail_to}
          style={{ colorScheme: isDarkMode() ? 'dark' : 'light' }}
          onChange={(e) => handleToChange(e.target.value || null)}
          className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[#32B1FF]"
        />
      </div>

      {isAtMaxRange && (
        <p className="text-xs text-orange-500 dark:text-orange-400 mb-3">
          Maximum range is 400 days — dates clamped to fit.
        </p>
      )}

      {/* ─── Summary line + CSV export ────────────────────────────────────── */}
      {detailData.data?.from && detailData.data?.to && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {detailRows.length} day{detailRows.length !== 1 ? "s" : ""} · {fmtDisplayDate(detailData.data.from)} → {fmtDisplayDate(detailData.data.to)}
          </p>
          {detailRows.length > 0 && (
            <button
              type="button"
              onClick={() => downloadCSV(detailRows)}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Export CSV"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* ─── States ──────────────────────────────────────────────────────── */}
      {detailData.loading && (
        <div className="h-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
      )}

      {detailData.error && (() => {
        // Map known RPC error codes to friendly copy; fall back generically.
        const errorCode = detailData.error?.code || detailData.error;
        const friendlyMessage =
          (typeof errorCode === "string" && RPC_ERROR_MESSAGES[errorCode]) ||
          "Something went wrong loading the data. Please try again.";
        return (
          <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20 mb-4">
            <p className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">
              Unable to load extended range data
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 mb-2">
              {friendlyMessage}
            </p>
          </div>
        );
      })()}

      {/* No data yet */}
      {!detailData.loading && !detailData.error && detailRows.length === 0 && (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
          Not enough data yet.
        </p>
      )}
      {/* ─── Paginated table (mirrors DataTable) ─────────────────────────── */}
      {!detailData.loading && !detailData.error && detailRows.length > 0 && (
        <div className="space-y-3">
          <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
            <table className="w-full text-sm responsive-card-table table-fixed min-w-[1255px]">
              <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`px-4 py-2.5 font-semibold cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 ${col.width} ${
                        col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                      }`}
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sortKey === col.key && (
                          <span className="text-gray-700 dark:text-gray-200">
                            {sortDir === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => (
                  <tr
                    key={row.date || i}
                    className="border-b border-gray-100 dark:border-gray-800 transition hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    {/* Date — weekday + short date */}
                    <td data-label="Date" className="px-4 py-2.5 text-left whitespace-nowrap">
                      <span className="text-gray-400 dark:text-gray-500 mr-1">
                        {dayOfWeek(row.date)}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {shortDate(row.date)}
                      </span>
                    </td>

                    {/* High temp */}
                    <td data-label="High" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums">
                      <span className="font-semibold text-orange-500 dark:text-orange-400">
                        {row.temp_high_f != null ? `${Math.round(row.temp_high_f)}°` : "—"}
                      </span>
                    </td>

                    {/* Normal (typical high) */}
                    <td data-label="Normal" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                      {row.typical_high_f != null ? `${Math.round(row.typical_high_f)}°` : "—"}
                    </td>

                    {/* vs Normal (signed delta) */}
                    <td data-label="vs Normal" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums">
                      {row.vs_typical_f != null ? (
                        <span className={`font-semibold ${row.vs_typical_f >= 0 ? "text-orange-500 dark:text-orange-400" : "text-blue-500 dark:text-blue-400"}`}>
                          {row.vs_typical_f >= 0 ? "+" : ""}{Number(row.vs_typical_f).toFixed(1)}°
                        </span>
                      ) : "—"}
                    </td>

                    {/* Low temp */}
                    <td data-label="Low" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums">
                      <span className="font-semibold text-blue-500 dark:text-blue-400">
                        {row.temp_low_f != null ? `${Math.round(row.temp_low_f)}°` : "—"}
                      </span>
                    </td>

                    {/* Rain % */}
                    <td data-label="Rain %" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums">
                      <span className={`font-semibold ${rainColor(row.precip_probability_pct)}`}>
                        {row.precip_probability_pct != null ? `${Math.round(row.precip_probability_pct)}%` : "—"}
                      </span>
                    </td>

                    {/* Rain Yrs (rain_day_pct_normal) */}
                    <td data-label="Rain Yrs" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                      {row.rain_day_pct_normal != null ? `${row.rain_day_pct_normal}%` : "—"}
                    </td>

                    {/* Snow (cm) */}
                    <td data-label="Snow" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                      {row.snow_cm != null ? `${row.snow_cm} cm` : "—"}
                    </td>

                    {/* Precip (mm) */}
                    <td data-label="Precip" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                      {row.precip_mm != null ? `${row.precip_mm} mm` : "—"}
                    </td>

                    {/* Wind */}
                    <td data-label="Wind" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                      {row.wind_max_mph != null ? row.wind_max_mph : "—"}
                    </td>

                    {/* Conditions */}
                    <td data-label="Conditions" className="px-4 py-2.5 text-left text-gray-500 dark:text-gray-400 truncate">
                      {row.weather != null ? row.weather : "—"}
                    </td>

                    {/* Source */}
                    <td data-label="Source" className="px-4 py-2.5 text-center whitespace-nowrap">
                      <span className={row.source === "captured" ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"}>
                        {row.source === "captured" ? "Captured" : "Archive"}
                      </span>
                    </td>

                    {/* Sessions */}
                    <td data-label="Sessions" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                      {fmtNum(row.sessions)}
                    </td>

                    {/* Bookings */}
                    <td data-label="Bookings" className="px-4 py-2.5 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap font-mono tabular-nums">
                      {fmtNum(row.bookings)}
                    </td>

                    {/* Revenue */}
                    <td data-label="Revenue" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {fmtCurrency(row.revenue)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ThinScroll>

          {/* Pagination — shared component, same as DataTable / GA Sources table */}
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="days"
          />
        </div>
      )}
    </div>
  );
};

export default DetailRangePanel;
